import { FC, ReactNode, useState, useRef, useCallback } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import cn from 'classnames';
import dibsCss from 'dibs-css';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { Button } from 'dibs-elements/src/Button';

type ConfirmModalProps = {
    isOpen: boolean;
    onClose: () => void;
    title: ReactNode;
    isBusy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    children: ReactNode;
    confirmLabel?: ReactNode;
};

export const ConfirmModal: FC<ConfirmModalProps> = props => {
    const { isOpen, onClose, title, isBusy, onConfirm, onCancel, children, confirmLabel } = props;

    return (
        <ModalContainer isOpen={isOpen} onClose={onClose} modalPosition="center">
            <ModalBody>
                <BarHeader title={title} />
                <div className={cn(dibsCss.mbLarge, dibsCss.textCenter, dibsCss.sassyFontBody)}>
                    {children}
                </div>
                <div
                    className={cn(
                        dibsCss.flex,
                        dibsCss.justifyCenter,
                        dibsCss.gapSmall,
                        dibsCss.plLarger,
                        dibsCss.prLarger
                    )}
                >
                    <Button
                        onClick={onCancel}
                        disabled={isBusy}
                        type="secondary"
                        size="medium"
                        fullWidth
                    >
                        <FormattedMessage
                            id="dibs-payment.ConfirmModal.cancelLabel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                    <Button onClick={onConfirm} disabled={isBusy} size="medium" fullWidth>
                        {isBusy && (
                            <Spinner
                                black
                                size="tiny"
                                containerClass={cn(
                                    dibsCss.inlineBlock,
                                    dibsCss.mrSmall,
                                    dibsCss.alignSub
                                )}
                            />
                        )}
                        <span>
                            {confirmLabel || (
                                <FormattedMessage
                                    id="dibs-payment.ConfirmModal.confirmLabel"
                                    defaultMessage="Confirm"
                                />
                            )}
                        </span>
                    </Button>
                </div>
            </ModalBody>
        </ModalContainer>
    );
};

type PartialModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onCancel: () => void;
};
type ModalPromptFunction = () => Promise<boolean>;
type ModalCloseFunction = () => void;
export function useConfirmModalProps(): [
    PartialModalProps,
    ModalPromptFunction,
    ModalCloseFunction
] {
    const [isOpen, setIsOpen] = useState(false);
    const modalResolveCallback = useRef<((result: boolean) => void) | null>(null);

    function promptConfirmation(): Promise<boolean> {
        setIsOpen(true);
        return new Promise(resolve => {
            modalResolveCallback.current = resolve;
        });
    }

    const resolvePromise = useCallback(
        (value: boolean) => {
            if (modalResolveCallback.current) {
                modalResolveCallback.current(value);
                modalResolveCallback.current = null;
            }
        },
        [modalResolveCallback]
    );

    const onConfirm = useCallback(() => {
        resolvePromise(true);
    }, [resolvePromise]);

    const onCancel = useCallback(() => {
        resolvePromise(false);
        setIsOpen(false);
    }, [resolvePromise]);

    const onClose = useCallback(() => {
        onCancel();
    }, [onCancel]);

    return [{ isOpen, onConfirm, onCancel, onClose }, promptConfirmation, onClose];
}
